import { REQUIRED_MESSAGE } from "@/utils/constant";
import { axiosInstance } from "@/utils/lib/axios";
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { RFSData } from "./rfs";
import { generateParams } from "@/utils/helper/generateParams";
import { MasterParamData } from "./master-param";

const integrationGPONPayloadSchema = z.object({
  deviceCode: z.string().min(1, { message: REQUIRED_MESSAGE }),
  rfs: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
});
type IntegrationGPONPayloadSchema = z.infer<
  typeof integrationGPONPayloadSchema
>;

interface IntegrationGPONHPMData {
  id: string;
  deviceCode: string;
  status: string;
  updatedDate: string;
}

/* ========================= *
 * READ Integration GPON HPM *
 * ========================= */

interface GenerateHPDBQueryKey {
  rfsId: string;
}

async function getIntegrationGPONHPMs(
  queryFunctionContext: QueryFunctionContext,
): Promise<IntegrationGPONHPMData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, integrationGPONHPMKey] = queryKey;
  const { rfsId } = integrationGPONHPMKey as GenerateHPDBQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/integration-gpon-hpm" + `?rfs.id=${rfsId ?? ""}`,
  );
  return response.data.data.content;
}
function useIntegrationGPONHPMs(integrationGPONHPMKey: GenerateHPDBQueryKey) {
  const { rfsId } = integrationGPONHPMKey;
  return useQuery({
    queryKey: ["integration-gpon-hpms", { rfsId }],
    queryFn: getIntegrationGPONHPMs,
  });
}

/* =========================== *
 * CREATE Integration GPON HPM *
 * =========================== */

async function createIntegrationGPONHPM(data: IntegrationGPONPayloadSchema) {
  const response = await axiosInstance.post(
    "/api/crud/integration-gpon-hpm",
    data,
  );
  return response.data;
}
function useIntegrationGPONHPMCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: IntegrationGPONPayloadSchema }) =>
      createIntegrationGPONHPM(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["integration-gpon-hpms"],
      });
    },
  });
}

/* ================ *
 * Bulk Insert GPON *
 * ================ */

async function insertBulkGPONHPM(id: string) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/processsendhpm/gpon/bulk-insert/${id}`,
  );

  return response.data;
}
function useBulkInsertGPONHPM() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => insertBulkGPONHPM(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["integration-gpon-hpms"],
      });
    },
  });
}

/* ================== *
 * GET Memo Active *
 * ================== */

const integrationMemoActivePayloadSchema = z.object({
  dateLm: z.string().min(1, { message: REQUIRED_MESSAGE }),
  dateMemoActive: z.string().min(1, { message: REQUIRED_MESSAGE }),
  footage: z.string().min(1, { message: REQUIRED_MESSAGE }),
  noMemo: z.string().min(1, { message: REQUIRED_MESSAGE }),
  remarks: z.string().min(1, { message: REQUIRED_MESSAGE }),
  rfs: z.object({
    dateFeederDone: z.string().min(1, { message: REQUIRED_MESSAGE }),
    dateOltDone: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
  userApprover: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
});
type IntegrationMemoActivePayloadSchema = z.infer<
  typeof integrationMemoActivePayloadSchema
>;

interface IntegrationMemoActiveData {
  id: string;
  updatedDate: string;
  noMemo: string;
  remarks: string;
  footage: string;
  dateLm: string;
  dateMemoActive: string;
  status: string;
  userApprover: UserApproverData;
  rfs: RFSData;
  firstServcoDesc: string;
  sendMemoActive: boolean;
  contractId: string;
}

interface UserApproverData {
  id: string;
  value: string;
}

async function getIntegrationMemoActives(
  queryFunctionContext: QueryFunctionContext,
): Promise<IntegrationMemoActiveData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, integrationMemoActiveData] = queryKey;
  const { rfsId } = integrationMemoActiveData as GenerateHPDBQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/integration-memo-active" + `?rfs.id=${rfsId ?? ""}`,
  );
  return response.data.data.content;
}
function useIntegrationMemoActives(
  integrationGPONHPMKey: GenerateHPDBQueryKey,
  options?: {
    staleTime?: number;
  },
) {
  const { rfsId } = integrationGPONHPMKey;
  return useQuery({
    ...options,
    queryKey: ["integration-memo-actives", { rfsId }],
    queryFn: getIntegrationMemoActives,
  });
}

/* ============================== *
 * Update Send Memo Active Status *
 * ============================== */

async function updateSendMemoActive(
  id: string,
  data: { sendMemoActive: boolean },
) {
  const response = await axiosInstance.post(
    "/api/crud/integration-memo-active/" + id,
    data,
  );
  return response.data;
}
function useUpdateSendMemoActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        sendMemoActive: boolean;
      };
    }) => updateSendMemoActive(id, data),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["integration-memo-actives"],
      });
    },
  });
}

/* ================== *
 * CREATE Memo Active *
 * ================== */

const memoActivePayloadSchema = z.object({
  dateLm: z.string().min(1, { message: REQUIRED_MESSAGE }),
  footage: z.string().min(1, { message: REQUIRED_MESSAGE }),
  remarks: z.string().optional(),
  rfs: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
    dateFeederDone: z.string().optional(),
    dateOltDone: z.string().optional(),
  }),
  userApprover: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
  firstServcoDesc: z.string().min(1, { message: REQUIRED_MESSAGE }),
  contractId: z.string().min(1, { message: REQUIRED_MESSAGE }),
});
type MemoActivePayloadSchema = z.infer<typeof memoActivePayloadSchema>;

async function createMemoActive(data: MemoActivePayloadSchema) {
  const response = await axiosInstance.post(
    "/api/crud/integration-memo-active",
    data,
  );
  return response.data;
}
function useCreateMemoActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: MemoActivePayloadSchema }) =>
      createMemoActive(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["integration-memo-actives"],
      });
    },
  });
}

/* ============================== *
 * CREATE Integration Memo Active *
 * ============================== */

async function createIntegrationMemoActive(id: string) {
  const response = await axiosInstance.post(
    "/bpmn/Rfs/processsendhpm/memo-active/insert-memo-active/" + id,
  );
  return response.data;
}
function useIntegrationMemoActiveCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => createIntegrationMemoActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["integration-memo-actives"],
      });
    },
  });
}

/* ====================== *
 * Send Memo Active Email *
 * ====================== */

async function sendMemoActiveEmail(id: string) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/processsendhpm/${id}/send-email-memo-active`,
  );
  return response.data;
}
function useSendMemoActiveEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => sendMemoActiveEmail(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["integration-memo-actives"],
      });
    },
  });
}

/* ============ *
 * GET Homepass *
 * ============ */

interface IntegrationHomepassData {
  id: string;
  status: string;
  managementArea: string;
  memoTypeDesc: string;
  updatedDate: string;
}

async function getIntegrationHomepass(
  queryFunctionContext: QueryFunctionContext,
): Promise<IntegrationHomepassData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, integrationHomepassKey] = queryKey;
  const { rfsId } = integrationHomepassKey as GenerateHPDBQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/integration-homepass" + `?rfs.id=${rfsId ?? ""}`,
  );
  return response.data.data.content;
}
function useIntegrationHomepass(integrationHomepassKey: GenerateHPDBQueryKey) {
  const { rfsId } = integrationHomepassKey;
  return useQuery({
    queryKey: ["integration-homepass", { rfsId }],
    queryFn: getIntegrationHomepass,
  });
}

/* =============== *
 * CREATE Homepass *
 * =============== */

const integrationHomepassPayloadSchema = z.object({
  managementArea: z.string().min(1, { message: REQUIRED_MESSAGE }),
  rfs: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
  memoTypeDesc: z.string().min(1, { message: REQUIRED_MESSAGE }),
});
type IntegrationHomepassPayloadSchema = z.infer<
  typeof integrationHomepassPayloadSchema
>;

async function createIntegrationHomepass(
  data: IntegrationHomepassPayloadSchema,
) {
  const response = await axiosInstance.post(
    "/api/crud/integration-homepass",
    data,
  );
  return response.data;
}
function useIntegrationHomepassCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: IntegrationHomepassPayloadSchema }) =>
      createIntegrationHomepass(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["integration-homepass"],
      });
    },
  });
}

/* ======== *
 * GET HPDB *
 * ======== */

interface IntegrationHPDBData {
  id: string;
  updatedDate: string;
  demographic: string;
  ownership: string;
  vendorName: string;
  serviceable: string;
  homepassId: string;
  partnerRfsDate: string;
  status: string;
  rfs: RFSData;
  requestId: string;
}

async function getIntegrationHPDBs(
  queryFunctionContext: QueryFunctionContext,
): Promise<IntegrationHPDBData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, integrationHPDBKey] = queryKey;
  const { rfsId } = integrationHPDBKey as GenerateHPDBQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/integration-hpdb" + `?rfs.id=${rfsId ?? ""}`,
  );
  return response.data.data.content;
}
function useIntegrationHPDBs(integrationHPDBKey: GenerateHPDBQueryKey) {
  const { rfsId } = integrationHPDBKey;
  return useQuery({
    queryKey: ["integration-hpdbs", { rfsId }],
    queryFn: getIntegrationHPDBs,
  });
}

/* ======== *
 * CREATE HPDB *
 * ======== */

const integrationHPDBPayloadSchema = z.object({
  demographic: z.string().min(1, { message: REQUIRED_MESSAGE }),
  homepassId: z.string().optional(),
  ownership: z.string().optional(),
  partnerRfsDate: z.string().optional(),
  rfs: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
  serviceable: z.string().min(1, { message: REQUIRED_MESSAGE }),
  status: z.string().min(1, { message: REQUIRED_MESSAGE }),
  vendorName: z.string().optional(),
});
type IntegrationHPDBPayloadSchema = z.infer<
  typeof integrationHPDBPayloadSchema
>;

async function createIntegrationHPDB(data: IntegrationHPDBPayloadSchema) {
  const response = await axiosInstance.post("/api/crud/integration-hpdb", data);
  return response.data;
}
function useIntegrationHPDBCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: IntegrationHPDBPayloadSchema }) =>
      createIntegrationHPDB(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["integration-hpdbs"],
      });
    },
  });
}

/* =================== *
 * Bulk Insert Site-ID *
 * =================== */

async function insertBulkSiteID(id: string) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/processsendhpm/site-id/bulk-insert/${id}`,
  );
  return response.data;
}
function useBulkInsertSiteID() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => insertBulkSiteID(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bulk-insert-site-id"],
      });
    },
  });
}

/* ============================== *
 * READ Integration Fibernode FDT *
 * ============================== */

interface IntegrationFibernodeFDTData {
  id: string;
  status: string;
  updatedDate: string;
}

async function getIntegrationFibernodeFDTs(
  queryFunctionContext: QueryFunctionContext,
): Promise<IntegrationFibernodeFDTData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, integrationFibernodeFDTKey] = queryKey;
  const { rfsId } = integrationFibernodeFDTKey as GenerateHPDBQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/integration-fibernode-fdt" + `?rfs.id=${rfsId ?? ""}`,
  );
  return response.data.data.content;
}
function useIntegrationFibernodeFDTs(
  integrationFibernodeFDTKey: GenerateHPDBQueryKey,
) {
  const { rfsId } = integrationFibernodeFDTKey;
  return useQuery({
    queryKey: ["integration-fibernode-fdt", { rfsId }],
    queryFn: getIntegrationFibernodeFDTs,
  });
}

/* ============================== *
 * INSERT Integration Fibernode FDT *
 * ============================== */

async function insertIntegrationFibernodeFDT(id: string) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/processsendhpm/fibernode/insert-data-with-fdt/${id}`,
  );
  return response.data;
}
function useInsertIntegrationFibernodeFDT() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => insertIntegrationFibernodeFDT(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["integration-fibernode-fdt"],
      });
    },
  });
}

/* ======================= *
 * READ Get By Device Code *
 * ======================= */

interface DeviceCodeData {
  deviceCode: number;
  deviceName: string;
  deviceIP: string;
}

interface DeviceCodeList {
  data: DeviceCodeData[];
}

type DeviceCodeQueryKey = {
  deviceName?: string;
};

async function getDeviceCodes(
  queryFunctionContext: QueryFunctionContext,
): Promise<DeviceCodeList> {
  const { queryKey } = queryFunctionContext;
  const [_, deviceCodeQueryKey] = queryKey;

  const params = generateParams(deviceCodeQueryKey as DeviceCodeQueryKey);

  const response = await axiosInstance.get(
    "/api/hpm-mirror/device/get-by-device-code" + params,
  );
  return response.data;
}
function useDeviceCodes(
  deviceCodeQueryKey: DeviceCodeQueryKey = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    ...options,
    queryKey: ["device-code", deviceCodeQueryKey],
    queryFn: getDeviceCodes,
  });
}

/* ==================== *
 * READ Management Area *
 * ==================== */

interface ManagementAreaData {
  mgt: number;
}

interface ManagementAreaList {
  data: ManagementAreaData[];
}

type ManagementAreaQueryKey = {
  managementAreaId?: string;
  pageSize?: number;
};

async function getManagementAreas(
  queryFunctionContext: QueryFunctionContext,
): Promise<ManagementAreaList> {
  const { queryKey } = queryFunctionContext;
  const [_, managementAreaQueryKey] = queryKey;
  const params = generateParams({
    pageSize: "100",
    ...(managementAreaQueryKey as ManagementAreaQueryKey),
  });

  const response = await axiosInstance.get(
    "/api/hpm-mirror/mgt/get-area" + params,
  );
  return response.data;
}
function useManagementAreas(
  managementAreaQueryKey: ManagementAreaQueryKey = {},
) {
  return useQuery({
    queryKey: ["management-area", managementAreaQueryKey],
    queryFn: getManagementAreas,
  });
}

/* ==================== *
 * READ First Servco ID *
 * ==================== */

interface FirstServcoIDData {
  Id: number;
  Description: string;
}

interface FirstServcoIDList {
  data: FirstServcoIDData[];
}

async function getFirstServcoID(): Promise<FirstServcoIDList> {
  // Get data first-servco default pageSize 100
  const response = await axiosInstance.get(
    "/api/hpm-mirror/first-servco/get-first-servco-id?pageSize=100",
  );
  return response.data;
}
function useFirstServcoID() {
  return useQuery({
    queryKey: ["first-servco-id"],
    queryFn: getFirstServcoID,
  });
}

/* ==================== *
 *    READ Memo Type    *
 * ==================== */

interface MemoTypeData {
  memoTypeId: number;
  memoTypeDesc: string;
}

interface MemoTypeList {
  data: MemoTypeData[];
}

async function getMemoTypes(): Promise<MemoTypeList> {
  const response = await axiosInstance.get(
    "/api/hpm-mirror/memo-type/get-memo-type",
  );
  return response.data;
}
function useMemoTypes() {
  return useQuery({
    queryKey: ["memo-type"],
    queryFn: getMemoTypes,
  });
}

/* ==================== *
 * DOWNLOAD Memo Active *
 * ==================== */

async function downloadMemoActive(id: string, name?: string) {
  const response = await axiosInstance.get(
    `/bpmn/Rfs/processsendhpm/integrationMemoActive/download/${id}`,
    {
      responseType: "blob",
    },
  );
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  const contentDisposition = response.request.getResponseHeader(
    "content-disposition",
  );
  const filename =
    name ??
    (contentDisposition
      ? contentDisposition.split("filename=")[1]
      : `${id}.xlsx`);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
function useDownloadMemoActive() {
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) =>
      downloadMemoActive(id, name),
  });
}

/* ============= *
 * DOWNLOAD HPDB *
 * ============= */

async function downloadHPDB(id: string, name?: string) {
  const response = await axiosInstance.get(
    `/bpmn/Rfs/processsendhpm/hpdb/gen-hpbd-hpm/${id}`,
    {
      responseType: "blob",
    },
  );
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  const contentDisposition = response.request.getResponseHeader(
    "content-disposition",
  );
  const filename =
    name ??
    (contentDisposition
      ? contentDisposition.split("filename=")[1]
      : `${id}.pdf`);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
function useDownloadHPDB() {
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) =>
      downloadHPDB(id, name),
  });
}

async function downloadHPDBKMZ(id: string, name?: string) {
  const response = await axiosInstance.get(
    `/bpmn/Rfs/processsendhpm/hpdb/gen-hpbd-hpm/${id}/kmz`,
    {
      responseType: "blob",
    },
  );
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  const contentDisposition = response.request.getResponseHeader(
    "content-disposition",
  );
  const filename =
    name ??
    (contentDisposition
      ? contentDisposition.split("filename=")[1]
      : `${id}.kmz`);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
function useDownloadHPDBKMZ() {
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) =>
      downloadHPDBKMZ(id, name),
  });
}

/* ================== *
 * UPDATE MEMO Active *
 * ================== */

async function updateIntegrationMemoActive(id: string) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/processsendhpm/hpdb/gen-hpbd-hpm/${id}/memo`,
  );
  return response.data;
}
function useUpdateIntegrationMemoActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => updateIntegrationMemoActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["gen-hpdb-hpm-memo"],
      });
    },
  });
}

/* ===================================== *
 * UPDATE STATUS INTEGRATION SUBMISSION *
 * ===================================== */
const updateRFSSubmissionPayloadSchema = z.object({
  statusRfsSubmissionLms: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
  resultTotalResidenceHp: z
    .number()
    .min(1, { message: REQUIRED_MESSAGE })
    .or(z.string().min(1, { message: REQUIRED_MESSAGE }))
    .pipe(z.coerce.number()),
  resultTotalBizzHp: z
    .number()
    .min(1, { message: REQUIRED_MESSAGE })
    .or(z.string().min(1, { message: REQUIRED_MESSAGE }))
    .pipe(z.coerce.number()),
  resultTotalHpRfs: z
    .number()
    .min(1, { message: REQUIRED_MESSAGE })
    .or(z.string().min(1, { message: REQUIRED_MESSAGE }))
    .pipe(z.coerce.number()),
  resultDate: z
    .string({ invalid_type_error: REQUIRED_MESSAGE })
    .min(1, { message: REQUIRED_MESSAGE }),
  resultRemarks: z.string().min(1, { message: REQUIRED_MESSAGE }),
});
type UpdateRFSSubmissionPayloadSchema = z.infer<
  typeof updateRFSSubmissionPayloadSchema
>;

async function updateRFSSubmission(
  id: string,
  data: UpdateRFSSubmissionPayloadSchema,
) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/updatedRfsSubmission/${id}`,
    data,
  );
  return response.data;
}
function useUpdateRFSSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateRFSSubmissionPayloadSchema;
    }) => updateRFSSubmission(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["update-rfs-submission"],
      });
    },
  });
}

/* ================ *
 * GET DATA for PDF *
 * ================ */

type PDFData = {
  approved: string;
  approvedTenant: string;
  checked: string;
  clusterId: string;
  clusterName: string;
  date: string;
  descriptions: MasterParamData[];
  district: string;
  drawn: string;
  fdtId: string;
  projectCode: string;
  rfsId: string;
  rt: string;
  rw: string;
  symbolDescriptions: MasterParamData[];
  totalHpRfs: number;
  totalFat: number;
  documentMaps: string;
  originalFileName: string;
  fileName: string;
};

interface PDFQueryKey {
  rfsId: string;
}

async function getDataPDF(
  queryFunctionContext: QueryFunctionContext,
): Promise<PDFData> {
  const { queryKey } = queryFunctionContext;
  const [_, pdfQueryKey] = queryKey;
  const { rfsId } = pdfQueryKey as PDFQueryKey;

  const response = await axiosInstance.get(
    `/bpmn/Rfs/processsendhpm/${rfsId}/get-data-pdf`,
  );
  return response.data.data;
}
function useGetDataPDF(pdfQueryKey: PDFQueryKey) {
  const { rfsId } = pdfQueryKey;
  return useQuery({
    queryKey: ["kmzpdf", { rfsId }],
    queryFn: getDataPDF,
  });
}

/* ========== *
 * POST IMAGE *
 * ========== */

const mediaMapPayloadSchema = z.object({
  file: z.instanceof(File, { message: REQUIRED_MESSAGE }),
  target: z.string().min(1, { message: REQUIRED_MESSAGE }),
});
type MediaMapPayloadSchema = z.infer<typeof mediaMapPayloadSchema>;

async function createMediaMaps(
  id: string,
  data: MediaMapPayloadSchema,
): Promise<RFSData> {
  const response = await axiosInstance.postForm(
    `/bpmn/Rfs/processsendhpm/${id}/upload/maps`,
    data,
  );
  return response.data.data;
}
function useMediaMapCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MediaMapPayloadSchema }) =>
      createMediaMaps(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["media-map"],
      });
    },
  });
}

export type {
  IntegrationGPONHPMData,
  IntegrationGPONPayloadSchema,
  IntegrationMemoActivePayloadSchema,
  IntegrationHomepassPayloadSchema,
  IntegrationHPDBPayloadSchema,
  MemoActivePayloadSchema,
  UpdateRFSSubmissionPayloadSchema,
  PDFData,
};
export {
  integrationGPONPayloadSchema,
  integrationMemoActivePayloadSchema,
  integrationHomepassPayloadSchema,
  integrationHPDBPayloadSchema,
  updateRFSSubmissionPayloadSchema,
  memoActivePayloadSchema,
  useIntegrationGPONHPMs,
  useIntegrationGPONHPMCreate,
  useBulkInsertGPONHPM,
  useIntegrationMemoActives,
  useIntegrationMemoActiveCreate,
  useSendMemoActiveEmail,
  useIntegrationHomepass,
  useIntegrationHomepassCreate,
  useIntegrationHPDBs,
  useIntegrationHPDBCreate,
  useBulkInsertSiteID,
  useIntegrationFibernodeFDTs,
  useInsertIntegrationFibernodeFDT,
  useDeviceCodes,
  useManagementAreas,
  useDownloadMemoActive,
  useCreateMemoActive,
  useDownloadHPDB,
  useDownloadHPDBKMZ,
  useUpdateIntegrationMemoActive,
  useUpdateRFSSubmission,
  useFirstServcoID,
  useMemoTypes,
  useGetDataPDF,
  useMediaMapCreate,
  useUpdateSendMemoActive,
};
